/**
 * BookCard Component - Enhanced Student Marketplace Design
 * 
 * Improvements over v1:
 * - #3: Content/file type badge (PDF, Notes, Slides, etc.)
 * - #4: PAID/FREE labels instead of BUY
 * - #5: Preview fallback state
 * - #6: Better placeholder with title initials + book style
 * - #7: Trust signal (uploader name, city)
 * - #9: Engagement counts (views, saves)
 */

import React, { useState, forwardRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Heart, Download, ShoppingCart, Clock, Eye, Bookmark, FileText, Star } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { formatMoney } from '../../utils/formatters';
import { trackOpen, trackSave, trackApplyClick } from '../../utils/tracker';
import WasslaImage from '../common/WasslaImage';

// Map tags → content type i18n key. Includes BOTH the new posting subtypes
// (notebook, pdf, magazine) AND legacy values (notes, slides, lab_manual,
// past_paper) so existing ads continue to render with the right label.
const TAG_TO_TYPE = {
  past_exam: 'sbp.pastPapers',
  past_paper: 'sbp.pastPapers',
  lecture_notes: 'sbp.lectureNotes',
  notes: 'sbp.summaries',          // legacy "notes" → render as Summary
  textbook: 'sbp.textbooks',
  lab_manual: 'sbp.labManual',
  summary: 'sbp.summaries',
  slides: 'sbp.slides',
  pdf: 'sbp.pdf',
  notebook: 'sbp.notebook',
  magazine: 'sbp.magazine',
};

// Infer content type from book data
function getContentType(book) {
  // Check tags first
  if (book.tags?.length) {
    for (const tag of book.tags) {
      const key = TAG_TO_TYPE[tag];
      if (key) return key;
    }
  }
  // Fallback based on type field
  if (book.type === 'notes') return 'sbp.notes';
  if (book.type === 'textbook') return 'sbp.textbook';
  // Default
  return 'sbp.document';
}

// Placeholder color palette based on first char of title
const PLACEHOLDER_COLORS = [
  ['#3B82F6', '#1D4ED8'], // blue
  ['#8B5CF6', '#6D28D9'], // purple
  ['#10B981', '#047857'], // green
  ['#F59E0B', '#D97706'], // amber
  ['#EF4444', '#DC2626'], // red
  ['#EC4899', '#BE185D'], // pink
  ['#06B6D4', '#0E7490'], // cyan
];

function getPlaceholderGradient(title) {
  const code = (title || 'B').charCodeAt(0) % PLACEHOLDER_COLORS.length;
  return PLACEHOLDER_COLORS[code];
}

function getInitials(title) {
  if (!title) return '?';
  const words = title.split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return words[0].substring(0, 2).toUpperCase();
}

// CTA configurations
const CTA_CONFIG = {
  free: { label_en: 'Get Free', label_ar: 'احصل مجاناً', icon: Download },
  sell: { label_en: 'Buy Now', label_ar: 'اشترِ الآن', icon: ShoppingCart },
  rent: { label_en: 'Rent', label_ar: 'استأجر', icon: Clock },
  download: { label_en: 'Download', label_ar: 'تحميل', icon: Download },
};

const BookCard = forwardRef(function BookCard({
  book,
  isSaved = false,
  onSave,
  onCTAClick,
  isRTL = false,
  lang = 'en',
  showCTA = true,
}, ref) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  
  const actualRTL = isRTL || i18n.language?.startsWith('ar');
  const actualLang = lang || (actualRTL ? 'ar' : 'en');
  
  // Mode & pricing
  const mode = book.mode || 'sell';
  const ctaConfig = CTA_CONFIG[mode] || CTA_CONFIG.sell;
  const CTAIcon = ctaConfig.icon;
  const isFree = mode === 'free' || book.price === 0;
  const priceDisplay = isFree 
    ? t('sbp.free')
    : formatMoney(book.price, book.currency || 'SDG', actualLang);
  
  // Content type
  const contentTypeKey = getContentType(book);
  
  // Level label
  const getLevelLabel = (levelKey) => {
    const levels = {
      secondary: { en: 'Secondary', ar: 'ثانوي' },
      university: { en: 'University', ar: 'جامعي' },
      postgrad: { en: 'Postgrad', ar: 'دراسات عليا' },
      vocational: { en: 'Vocational', ar: 'مهني' },
      other: { en: 'Other', ar: 'أخرى' },
    };
    const level = levels[levelKey];
    return level ? (actualRTL ? level.ar : level.en) : levelKey;
  };
  
  // City label
  const getCityLabel = (cityKey) => {
    const cities = {
      khartoum: { en: 'Khartoum', ar: 'الخرطوم' },
      omdurman: { en: 'Omdurman', ar: 'أم درمان' },
      khartoum_north: { en: 'Khartoum North', ar: 'الخرطوم بحري' },
      port_sudan: { en: 'Port Sudan', ar: 'بورتسودان' },
      wad_madani: { en: 'Wad Madani', ar: 'ود مدني' },
      kassala: { en: 'Kassala', ar: 'كسلا' },
      gedarif: { en: 'Gedarif', ar: 'القضارف' },
    };
    const city = cities[cityKey];
    return city ? (actualRTL ? city.ar : city.en) : cityKey;
  };
  
  // Condition label
  const getConditionLabel = (c) => {
    const m = { new: { en: 'New', ar: 'جديد' }, used_like_new: { en: 'Like New', ar: 'كالجديد' }, used_good: { en: 'Good', ar: 'جيد' }, used: { en: 'Used', ar: 'مستعمل' }, used_fair: { en: 'Fair', ar: 'مقبول' } };
    return m[c] ? (actualRTL ? m[c].ar : m[c].en) : c;
  };

  // Card click handler
  const handleCardClick = () => {
    trackOpen(book.id, book.category || 'books_notes');
    navigate(`/student-hub/books/${book.id}`, { state: { book } });
  };
  
  const handleSaveClick = (e) => {
    e.stopPropagation();
    trackSave(book.id, book.category || 'books_notes', !isSaved);
    onSave?.(book.id);
  };
  
  const handleCTAClick = (e) => {
    e.stopPropagation();
    trackApplyClick(book.id, book.category || 'books_notes', mode);
    onCTAClick?.(book);
  };
  
  // First level & subject from data
  const primaryLevel = Array.isArray(book.level) ? book.level[0] : book.level;
  const formatTag = (tag) => {
    if (!tag) return '';
    return tag.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };
  const primarySubject = book.tags?.[0] ? formatTag(book.tags[0]) : null;
  
  // Engagement metrics
  const views = book.views7d || book.views || 0;
  const saves = book.saves7d || book.saves || 0;
  
  // Placeholder gradient
  const title = actualRTL && book.title_ar ? book.title_ar : book.title;
  const [gradStart, gradEnd] = getPlaceholderGradient(title);
  const initials = getInitials(title);
  
  return (
    <div 
      ref={ref}
      className="book-card-v2"
      onClick={handleCardClick}
      dir={actualRTL ? 'rtl' : 'ltr'}
      data-testid={`book-card-${book.id}`}
      data-item-id={book.id}
    >
      {/* Thumbnail Container */}
      <div className="book-card-thumbnail">
        <div className="book-card-thumbnail-inner">
          {/* iter327 — WasslaImage owns the loading/loaded/error states.
              The branded "book" gradient + initials placeholder is still
              the canonical "no image at all" fallback because it carries
              richer signal (title initials + content type) than a generic
              fallback caption. */}
          {book.images?.[0] && !imageError ? (
            <WasslaImage
              src={book.images[0]}
              alt={title}
              fallbackType="book"
              testid={`book-card-image-${book.id}`}
              showCaption={false}
              onLoad={() => setImageLoaded(true)}
              onError={() => setImageError(true)}
            />
          ) : (
            /* #6 — Enhanced placeholder with initials + gradient */
            <div
              className="book-card-placeholder-v2"
              style={{ background: `linear-gradient(135deg, ${gradStart}, ${gradEnd})` }}
            >
              <span className="placeholder-initials">{initials}</span>
              <FileText size={16} className="placeholder-filetype-icon" />
              <span className="placeholder-type">{t(contentTypeKey)}</span>
            </div>
          )}
          
          {/* Gradient overlay */}
          <div className="book-card-gradient" />
        </div>
        
        {/* Save button (top-right) */}
        <button
          className={`book-card-save ${isSaved ? 'saved' : ''}`}
          onClick={handleSaveClick}
          aria-label={actualRTL ? 'حفظ' : 'Save'}
          data-testid={`save-btn-${book.id}`}
        >
          <Heart size={18} fill={isSaved ? 'currentColor' : 'none'} />
        </button>
        
        {/* #4 — PAID/FREE pill instead of BUY */}
        <span className={`book-card-type ${isFree ? 'type-free' : 'type-paid'}`} data-testid={`type-pill-${book.id}`}>
          {isFree ? t('sbp.free') : t('sbp.paid')}
        </span>
        
        {/* #3 — Content type badge (bottom-left of thumbnail) */}
        <span className="book-card-content-badge" data-testid={`content-badge-${book.id}`}>
          <FileText size={10} />
          {t(contentTypeKey)}
        </span>

        {/* Preview badge — only when preview_images exist */}
        {book.preview_images && book.preview_images.length > 0 && (
          <span className="book-card-preview-badge" data-testid={`preview-badge-${book.id}`}>
            <Eye size={10} />
            {actualRTL ? 'معاينة' : 'Preview'}
          </span>
        )}
      </div>
      
      {/* Content */}
      <div className="book-card-content">
        {/* Title */}
        <h3 className="book-card-title">{title}</h3>
        
        {/* #7 — Trust signal: author + city */}
        <div className="book-card-meta">
          {book.author && (
            <span className="book-card-author">{book.author}</span>
          )}
          {book.city && (
            <span className="book-card-city">{getCityLabel(book.city)}</span>
          )}
        </div>
        
        {/* Tags row (level + subject + condition) */}
        <div className="book-card-tags">
          {primaryLevel && (
            <Badge variant="outline" className="book-card-level-badge">
              {getLevelLabel(primaryLevel)}
            </Badge>
          )}
          {primarySubject && (
            <Badge variant="secondary" className="book-card-subject-badge">
              {primarySubject}
            </Badge>
          )}
          {book.condition && (
            <Badge variant="outline" className="book-card-condition-badge" data-testid={`condition-${book.id}`}>
              {getConditionLabel(book.condition)}
            </Badge>
          )}
        </div>
        
        {/* #9 — Engagement counts + Rating */}
        <div className="book-card-engagement-row">
          {book.average_rating > 0 && (
            <span className="book-card-rating" data-testid={`rating-${book.id}`}>
              <Star size={12} fill="#F59E0B" stroke="#F59E0B" />
              {book.average_rating.toFixed(1)}
              {book.reviews_count > 0 && <span className="rating-count">({book.reviews_count})</span>}
            </span>
          )}
          {(views > 0 || saves > 0) && (
            <div className="book-card-engagement" data-testid={`engagement-${book.id}`}>
              {views > 0 && (
                <span className="engagement-item">
                  <Eye size={12} />
                  {views}
                </span>
              )}
              {saves > 0 && (
                <span className="engagement-item">
                  <Bookmark size={12} />
                  {saves}
                </span>
              )}
            </div>
          )}
        </div>
        
        {/* Price row */}
        <div className="book-card-price-row">
          <span className={`book-card-price ${isFree ? 'free' : ''}`}>
            {priceDisplay}
          </span>
        </div>
        
        {/* CTA Button */}
        {showCTA && (
          <Button
            className={`book-card-cta ${isFree ? 'cta-free' : 'cta-buy'}`}
            onClick={handleCTAClick}
            data-testid={`cta-btn-${book.id}`}
          >
            <CTAIcon size={16} />
            {actualRTL ? ctaConfig.label_ar : ctaConfig.label_en}
          </Button>
        )}
        
        {/* Verified Seller badge */}
        {book.seller?.is_verified && (
          <div className="book-card-verified">
            <span>{actualRTL ? 'بائع موثق' : 'Verified Seller'}</span>
          </div>
        )}
      </div>
    </div>
  );
});

// Skeleton loader
export function BookCardSkeleton() {
  return (
    <div className="book-card-v2 skeleton">
      <div className="book-card-thumbnail">
        <div className="book-card-thumbnail-inner">
          <Skeleton className="book-card-skeleton" />
        </div>
      </div>
      <div className="book-card-content">
        <Skeleton className="h-5 w-4/5 mb-2" />
        <Skeleton className="h-3 w-3/5 mb-3" />
        <div className="flex gap-2 mb-3">
          <Skeleton className="h-5 w-16" />
          <Skeleton className="h-5 w-14" />
        </div>
        <Skeleton className="h-6 w-20 mb-3" />
        <Skeleton className="h-10 w-full" />
      </div>
    </div>
  );
}

export default BookCard;
